import {announce} from '@github-ui/aria-live'
import {useEffect, useRef} from 'react'

/**
 * Announce `message` when `active` turns true, and not again until it has turned false.
 *
 * The board pages by being scrolled rather than by anything a reader did, so a page
 * arriving is a status message: it appears without taking focus, and a reader who cannot
 * see the skeleton is otherwise never told.
 *
 * It announces rather than rendering a live region, which is the repository's preferred
 * mechanism and avoids the timing trap a region would walk straight into here. A region
 * has to be in the accessibility tree before its content changes, and every arrangement
 * available to this component puts the region and its text there in the same commit: the
 * ghost only mounts once a direction has something to say. There would also be one
 * region per cell, and a board paging six cells would have six polite regions mutating
 * at once, which assistive technology coalesces or drops.
 *
 * Once per transition, because the alternative is announcing again on every unrelated
 * re-render.
 */
export function useAnnounceOnce(message: string, active: boolean) {
  const announcedRef = useRef(false)

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- there is no handler to move this to. The board pages by being scrolled, so what changes is data the caller fetched and handed back, and announcing it synchronises an external system.
    if (!active) {
      announcedRef.current = false
      return
    }

    if (announcedRef.current) return

    announcedRef.current = true
    announce(message)
  }, [active, message])
}
